import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/shared/lib/analytics';
import { readConsent, rememberConsent, type Consent } from '@/shared/lib/analytics/consent';
import { useContentLocale } from '@/shared/lib/locale';
import { baseDomain, privacyPageUrl } from '@/shared/lib/site';
import { Button } from '@/shared/ui/Button';

/**
 * The question about cookies, at the foot of the window until it is answered: asked only
 * where the page has a counter (shared/lib/analytics). The answer is the whole domain's,
 * so the front page does not ask again (site/consent.ts), nor does this one.
 */
export function ConsentBanner() {
  const { t } = useTranslation();
  const locale = useContentLocale();
  const [answered, setAnswered] = useState(() => readConsent() !== null);
  if (answered || !analytics.configured) return null;

  const answer = (consent: Consent) => {
    rememberConsent(consent, baseDomain());
    setAnswered(true);
    // Agreed: the page shown now is the first one counted.
    if (consent === 'all') analytics.page();
  };

  return (
    <section
      aria-label={t('consent.label')}
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-2xl border border-line bg-surface p-4 shadow-lg sm:flex sm:items-center sm:gap-4"
    >
      <p className="text-sm">
        {t('consent.text')}{' '}
        <a href={privacyPageUrl(locale)} className="underline">
          {t('consent.policy')}
        </a>
      </p>
      <div className="mt-3 flex shrink-0 gap-2 sm:mt-0">
        <Button size="sm" onClick={() => answer('all')}>
          {t('consent.accept')}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => answer('necessary')}>
          {t('consent.necessary')}
        </Button>
      </div>
    </section>
  );
}
